<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { NButton, NEmpty, NSpin, NResult, NPagination } from 'naive-ui';
import ProviderCard from '../components/ProviderCard.vue';
import { fetchBookmarks, deleteBookmark } from '../services/bookmark';
import { authToken, isAuthenticated } from '../services/auth';

const route = useRoute();
const router = useRouter();

// State variables
const bookmarkedProviders = ref([]);
const loading = ref(true);
const error = ref(null);

// Pagination state
const currentPage = ref(1);
const pageSize = ref(10);
const totalItems = ref(0);
const totalPages = ref(1);

// Load bookmarks when component mounts
onMounted(async () => {
  if (!isAuthenticated.value) {
    error.value = 'Please log in to view your bookmarks';
    loading.value = false;
    return;
  }
  
  // Get page from URL if present
  if (route.query.page) {
    currentPage.value = parseInt(route.query.page) || 1;
  }
  
  await loadBookmarks();
});

// Function to load bookmarks
async function loadBookmarks() {
  try {
    loading.value = true;
    error.value = null;
    
    const response = await fetchBookmarks(
      authToken.value, 
      currentPage.value, 
      pageSize.value
    );
    
    // Extract bookmarks and pagination data
    if (response && response.bookmarks) {
      bookmarkedProviders.value = response.bookmarks;
      
      // Update pagination data
      if (response.pagination) {
        totalItems.value = response.pagination.total;
        totalPages.value = response.pagination.total_pages;
        currentPage.value = response.pagination.page;
        pageSize.value = response.pagination.page_size;
      }
      
    } else {
      // Legacy format fallback
      bookmarkedProviders.value = response;
    }
  } catch (err) {
    console.error('Failed to load bookmarks:', err);
    error.value = 'Failed to load bookmarks. Please try again later.';
    bookmarkedProviders.value = [];
  } finally {
    loading.value = false;
  }
}

// Handle page change
const handlePageChange = (page) => {
  currentPage.value = page;
  
  // Update URL with new page
  router.replace({ query: { page } });
  
  // Scroll to top
  window.scrollTo({ top: 0, behavior: 'smooth' });
  
  // Load bookmarks for the new page
  loadBookmarks();
};

// Function to remove a bookmark
async function unbookmarkProvider(providerId) {
  // Debug logging to understand what's being passed
  
  if (!providerId) {
    console.error('Cannot unbookmark: Provider ID is undefined');
    return;
  }
  
  try {
    await deleteBookmark(authToken.value, providerId);
        
    // Remove the bookmarked provider from the list
    bookmarkedProviders.value = bookmarkedProviders.value.filter(bookmark => {
      // Check both possible locations of the provider ID
      const id = bookmark.provider_id || (bookmark.provider && bookmark.provider.id);
      return id !== providerId;
    });
    
    // Update total count
    totalItems.value = Math.max(0, totalItems.value - 1);
    
    // If current page is now empty and it's not the first page, go to previous page
    if (bookmarkedProviders.value.length === 0 && currentPage.value > 1) {
      currentPage.value -= 1;
      await loadBookmarks();
    } else if (bookmarkedProviders.value.length === 0) {
      // If first page is empty, just update the display
      totalPages.value = 0;
    } else {
      // Recalculate total pages
      totalPages.value = Math.ceil(totalItems.value / pageSize.value);
    }
  } catch (err) {
    console.error('Failed to remove bookmark:', err);
  }
}

// Check if provider is bookmarked (always true in this view)
function isProviderBookmarked() {
  return true;
}
</script>

<template>
  <div class="bookmarks-container">
    <div class="bookmarks-header">
      <h1 class="text-2xl font-bold text-gray-800 mb-4">My Bookmarks</h1>
      <p class="text-gray-600 mb-6">Here are all the providers you've bookmarked.</p>
    </div>

    <!-- Results Summary -->
    <div v-if="!loading && bookmarkedProviders.length > 0" class="results-summary">
      <p>
        Showing {{ (currentPage - 1) * pageSize + 1 }}-{{ Math.min(currentPage * pageSize, totalItems) }} of {{ totalItems }} bookmarked providers
      </p>
    </div>

    <!-- Loading state -->
    <NSpin v-if="loading" class="flex justify-center py-12">
      <template #description>Loading your bookmarks...</template>
    </NSpin>

    <!-- Error state -->
    <NResult 
      v-else-if="error" 
      status="error" 
      title="Error Loading Bookmarks" 
      :description="error"
    >
      <template #footer>
        <NButton @click="loadBookmarks">Try Again</NButton>
      </template>
    </NResult>

    <!-- Empty state -->
    <NEmpty 
      v-else-if="bookmarkedProviders.length === 0" 
      description="You haven't bookmarked any providers yet."
    >
      <template #extra>
        <NButton type="primary" @click="$router.push('/search')">
          Explore Providers
        </NButton>
      </template>
    </NEmpty>

    <!-- Bookmarks list -->
    <div v-else>
      <div class="providers-grid">
        <ProviderCard
          v-for="bookmark in bookmarkedProviders"
          :key="bookmark.id || bookmark.bookmark_id"
          :provider="bookmark.provider"
          :showBookmarkButton="true"
          :isBookmarked="isProviderBookmarked()"
          @unbookmark="() => unbookmarkProvider(bookmark.provider_id || (bookmark.provider && bookmark.provider.id))"
        />
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="pagination-section">
        <NPagination
          v-model:page="currentPage"
          :page-count="totalPages"
          :page-size="pageSize"
          :item-count="totalItems"
          show-size-picker
          :page-sizes="[10, 20, 50]"
          @update:page="handlePageChange"
          @update:page-size="(size) => {
            pageSize = size;
            currentPage = 1;
            loadBookmarks();
          }"
        >
          <template #prefix="{ itemCount }">
            Total: {{ itemCount }}
          </template>
        </NPagination>
      </div>
    </div>
  </div>
</template>

<style scoped>
.bookmarks-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
}

.results-summary {
  text-align: center;
  margin-bottom: 1.5rem;
  color: #666;
  font-size: 0.95rem;
}

.results-summary p {
  margin: 0;
}

.providers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.pagination-section {
  display: flex;
  justify-content: center;
  padding: 2rem 0;
  margin-top: 2rem;
}

@media (max-width: 768px) {
  .providers-grid {
    grid-template-columns: 1fr;
  }
  
  .pagination-section {
    padding: 1rem 0;
  }
}
</style>